import React, { useState, useEffect, useRef } from 'react';
import './ServicesWhite.css';

const ServicesWhite = () => {
  const [activeService, setActiveService] = useState(1);
  const [animating, setAnimating] = useState(false);
  const wheelTimeoutRef = useRef(null);
  const isScrollingRef = useRef(false);
  const servicesSectionRef = useRef(null);
  const contentRef = useRef(null);
  const lastWheelTimeRef = useRef(0);

  const services = [
    {
      id: 1,
      number: '01.',
      title: 'Notre mission',
      color: '#4DCA8B',
      subtitle: 'Faire croître les ventes B2B durablement.',
      description: 'Nous accompagnons les entreprises dans leur développement commercial grâce à une approche scientifique, structurée et orientée résultats.',
      linkText: 'En savoir plus'
    },
    {
      id: 2,
      number: '02.',
      title: 'Notre engagement',
      color: '#FFA05E',
      subtitle: 'Des résultats concrets, une autonomie durable.',
      description: 'Nous analysons votre activité en profondeur, partageons notre expertise et vous guidons pour que vos performances commerciales deviennent mesurables et reproductibles.',
      linkText: 'En savoir plus'
    },
    {
      id: 3,
      number: '03.',
      title: 'Notre approche',
      color: '#FFD966',
      subtitle: 'Une prospection sur-mesure, orchestrée par des experts.',
      description: 'Notre collectif active les bons leviers (email, LinkedIn, contenu, calls…) pour déployer des campagnes multicanales, ultra-ciblées et optimisées selon votre secteur.',
      linkText: 'En savoir plus'
    }
  ];

  // Ajuster la hauteur de la section pour qu'elle prenne tout l'écran
  useEffect(() => {
    const adjustHeight = () => {
      if (servicesSectionRef.current) {
        const windowHeight = window.innerHeight;
        servicesSectionRef.current.style.height = `${windowHeight}px`;
      }
    };

    // Ajuster la hauteur initialement et à chaque redimensionnement
    adjustHeight();
    window.addEventListener('resize', adjustHeight);

    return () => {
      window.removeEventListener('resize', adjustHeight);
    };
  }, []);

  const handleServiceChange = (id) => {
    if (animating || id === activeService) return;
    
    setAnimating(true);
    
    // Animer la sortie du contenu actuel
    if (contentRef.current) {
      contentRef.current.style.opacity = '0';
      contentRef.current.style.transform = 'translateY(20px)';
    }
    
    // Changer le service après un court délai
    setTimeout(() => {
      setActiveService(id);
      
      // Animer l'entrée du nouveau contenu
      if (contentRef.current) {
        setTimeout(() => {
          contentRef.current.style.opacity = '1';
          contentRef.current.style.transform = 'translateY(0)';
          
          // Réinitialiser l'état d'animation après la transition
          setTimeout(() => {
            setAnimating(false);
          }, 500);
        }, 50);
      }
    }, 300);
  };

  const handleServiceClick = (id) => {
    handleServiceChange(id);
  };

  const handleServiceHover = (id) => {
    // Pour éviter que le survol ne déclenche des changements indésirables pendant le défilement
    if (!isScrollingRef.current && !animating) {
      handleServiceChange(id);
    }
  };

  const handleWheelEvent = (e) => {
    // Si on est sur la première slide et qu'on défile vers le haut, permettre le défilement normal
    if (activeService === 1 && e.deltaY < 0) {
      return; // Ne pas appeler preventDefault pour permettre le défilement normal
    }
    
    // Si on est sur la dernière slide et qu'on défile vers le bas, permettre le défilement normal
    if (activeService === services.length && e.deltaY > 0) {
      return; // Ne pas appeler preventDefault pour permettre le défilement normal
    }
    
    e.preventDefault();
    
    // Limiter la fréquence des événements de défilement
    const now = Date.now();
    if (now - lastWheelTimeRef.current < 500 || animating) {
      return;
    }
    
    lastWheelTimeRef.current = now;
    
    // Déterminer la direction du défilement (positif = vers le bas)
    if (e.deltaY > 0) {
      // Défilement vers le bas, passer à la slide suivante
      const nextSlide = Math.min(services.length, activeService + 1);
      if (nextSlide !== activeService) {
        handleServiceChange(nextSlide);
      }
    } else if (e.deltaY < 0) {
      // Défilement vers le haut, passer à la slide précédente
      const prevSlide = Math.max(1, activeService - 1);
      if (prevSlide !== activeService) {
        handleServiceChange(prevSlide);
      }
    }
  };

  useEffect(() => {
    const section = servicesSectionRef.current;
    
    if (section) {
      // Utiliser l'API addEventListener standard
      section.addEventListener('wheel', handleWheelEvent, { passive: false });
      
      return () => {
        section.removeEventListener('wheel', handleWheelEvent);
      };
    }
  }, [activeService, animating]);

  // Gérer la navigation avec les touches fléchées
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (animating) return;
      
      // Flèche vers le bas ou Page Down
      if (e.key === 'ArrowDown' || e.key === 'PageDown') {
        e.preventDefault();
        const nextSlide = Math.min(services.length, activeService + 1);
        if (nextSlide !== activeService) {
          handleServiceChange(nextSlide);
        }
      }
      // Flèche vers le haut ou Page Up
      else if (e.key === 'ArrowUp' || e.key === 'PageUp') {
        e.preventDefault();
        const prevSlide = Math.max(1, activeService - 1);
        if (prevSlide !== activeService) {
          handleServiceChange(prevSlide);
        }
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [activeService, animating]);

  const activeServiceData = services.find(service => service.id === activeService);

  return (
    <section className="services-white" ref={servicesSectionRef}>
      <div className="services-label">NOTRE VISION</div>
      <div className="services-container">
        <div className="services-menu">
          {services.map((service) => (
            <div 
              key={service.id} 
              className={`service-menu-item ${activeService === service.id ? 'active' : ''}`}
              onClick={() => handleServiceClick(service.id)}
              onMouseEnter={() => handleServiceHover(service.id)}
            >
              <div className="service-number">{service.number}</div>
              <div className="service-menu-title">{service.title}</div>
            </div>
          ))}
        </div>
        <div className="service-content" ref={contentRef} style={{ opacity: 1, transform: 'translateY(0)', transition: 'opacity 0.5s ease, transform 0.5s ease' }}>
          <h1 className="service-title" style={{ color: activeServiceData.color }}>
            {activeServiceData.title}
          </h1>
          <h2 className="service-subtitle">
            {activeServiceData.subtitle}
          </h2>
          <p className="service-description">
            {activeServiceData.description}
          </p>
          <div className="service-link-container">
            <a 
              href={`#${activeServiceData.title.toLowerCase().replace(' ', '-')}`} 
              className="service-link"
              style={{ color: activeServiceData.color }}
            >
              {activeServiceData.linkText}
              <span className="service-link-arrow">→</span>
            </a>
          </div>
        </div>
        <div className="service-graphic">
          <div className="service-pattern" style={{ borderColor: activeServiceData.color, transform: animating ? 'rotate(10deg) scale(0.95)' : 'rotate(0) scale(1)' }}></div>
        </div>
      </div>
    </section>
  );
};

export default ServicesWhite; 